import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { MessageSquarePlus, PanelLeft, Trash2 } from "lucide-react";
import { useState } from "react";
import { Chat } from "../components/Chat";
import { Spinner } from "../components/ui/primitives";
import { api, unwrap } from "../lib/api";
import { cn, newId } from "../lib/utils";

const STORAGE_KEY = "cafe.activeConversation";

export function ChatPage() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string>(() => localStorage.getItem(STORAGE_KEY) ?? newId());
  const [sidebar, setSidebar] = useState(false);

  const select = (id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setActiveId(id);
    setSidebar(false);
  };

  const list = useQuery({ queryKey: ["conversations"], queryFn: async () => unwrap(await api.conversations.$get()) });
  const convo = useQuery({
    queryKey: ["conversation", activeId],
    queryFn: async () => {
      const data = await unwrap(await api.conversations[":id"].$get({ param: { id: activeId } }));
      return { id: activeId, messages: data.messages as unknown as UIMessage[] };
    },
    staleTime: Infinity,
    // Drop the snapshot when leaving a conversation so coming back always reloads the latest messages.
    gcTime: 0,
  });

  const remove = async (id: string) => {
    await api.conversations[":id"].$delete({ param: { id } });
    await qc.invalidateQueries({ queryKey: ["conversations"] });
    if (id === activeId) select(newId());
  };

  return (
    <div className="flex h-full">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-20 w-72 border-r border-latte bg-foam p-3 transition-transform md:static md:translate-x-0",
          sidebar ? "translate-x-0 shadow-xl" : "-translate-x-full",
        )}
        aria-label="Conversaciones"
      >
        <button onClick={() => select(newId())} className="mb-3 flex w-full items-center gap-2 rounded-xl bg-espresso px-3 py-2 text-sm text-foam hover:bg-roast">
          <MessageSquarePlus className="size-4" /> Nueva conversación
        </button>
        <ul className="space-y-0.5 overflow-y-auto text-sm">
          {list.data?.conversations.map((c) => (
            <li key={c.id} className="group flex items-center">
              <button
                onClick={() => select(c.id)}
                className={cn("min-w-0 flex-1 truncate rounded-lg px-2.5 py-2 text-left hover:bg-latte/50", c.id === activeId && "bg-latte/70 font-medium")}
              >
                {c.title}
              </button>
              <button onClick={() => remove(c.id)} className="invisible rounded p-1 text-roast/60 group-hover:visible hover:text-red-700" aria-label="Borrar conversación">
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
          {list.data?.conversations.length === 0 && <li className="px-2.5 py-2 text-roast/60">Aún no hay conversaciones.</li>}
        </ul>
      </aside>
      {sidebar && <div className="fixed inset-0 z-10 bg-espresso/30 md:hidden" onClick={() => setSidebar(false)} />}
      <section className="relative flex min-w-0 flex-1 flex-col">
        <button onClick={() => setSidebar(true)} className="absolute top-2 left-2 z-10 rounded-lg bg-foam/90 p-1.5 text-roast shadow-sm md:hidden" aria-label="Ver conversaciones">
          <PanelLeft className="size-4" />
        </button>
        {convo.isLoading || !convo.data ? (
          <div className="grid flex-1 place-items-center">
            <Spinner />
          </div>
        ) : (
          <Chat key={activeId} conversationId={activeId} initialMessages={convo.data.messages} />
        )}
      </section>
    </div>
  );
}
